// Client Portal — RAG / Knowledge Base hub: RAGChat portal + all KB/RAG
// panels (KBPanel, ChatPanel, BriefingsPanel, RAGDocuments, RAGChatPanel,
// KBReviewPanel, VectorSearch, GapTasksPanel, QuickQAModal) + KB constants.
// Extracted from App.js. KBPanel/BriefingsPanel/KB_ROLE_LEVELS/KB_TYPE_CONFIG
// are also consumed by PAS/Aftercare/VoiceIQ, so they are exported.
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  AlertTriangle,
  Archive,
  Bell,
  Bot,
  Brain,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ClipboardList,
  Database,
  Edit3,
  FileText,
  History,
  ImagePlus,
  Loader2,
  MessageSquare,
  Palette,
  Pencil,
  Plus,
  PlusCircle,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  Send,
  Sliders,
  Sparkles,
  Star,
  ThumbsDown,
  ThumbsUp,
  ToggleLeft,
  ToggleRight,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { AUTH_URL, authFetch, getToken } from "../lib/api";
import { showToast } from "../lib/toast";
import {
  DEFAULT_QUICK_PROMPTS,
  DEFAULT_RAG_COLORS,
  autoTitle,
  createConversation,
  deleteConversation,
  getRagConfig,
  getUserInitials,
  loadConversations,
  parseMessageText,
  resizeImageToFile,
  trackEvent,
  updateConversation,
} from "../lib/ragConfig";
import { RAGCustomize } from "./RAGCustomize";
import { RAGPromptStudio } from "./RAGPromptStudio";
import DOMPurify from "dompurify";
import { SystemBanner } from "../components/SystemBanner";

const SCHEDULE_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

const DELIVERY_OPTIONS = [
  { value: "email", label: "Email" },
  { value: "slack", label: "Slack webhook" },
  { value: "webhook", label: "Webhook URL" },
];

const emptyBriefing = (facility_id) => ({
  facility_id,
  briefing_name: "",
  schedule: "weekly",
  delivery_method: "email",
  delivery_destination: "",
  query_topics: [],
  summary_prompt: "",
  is_active: true,
});

export const BriefingsPanel = ({ facility }) => {
  const fid = facility.facility_id;
  const [briefings, setBriefings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | briefing object
  const [saving, setSaving] = useState(false);
  const [newTopic, setNewTopic] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    const data = await authFetch(
      `/my/briefings?facility_id=${encodeURIComponent(fid)}`,
    );
    if (Array.isArray(data)) setBriefings(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fid]); // load is stable within this render cycle

  const openNew = () => setEditing(emptyBriefing(fid));

  const save = async () => {
    if (!editing.briefing_name.trim() || !editing.delivery_destination.trim())
      return;
    setSaving(true);
    if (editing.id) {
      await authFetch(`/my/briefings/${editing.id}`, {
        method: "PUT",
        body: JSON.stringify(editing),
      });
    } else {
      await authFetch("/my/briefings", {
        method: "POST",
        body: JSON.stringify(editing),
      });
    }
    setSaving(false);
    setEditing(null);
    load();
  };

  const toggleActive = async (b) => {
    await authFetch(`/my/briefings/${b.id}`, {
      method: "PUT",
      body: JSON.stringify({ is_active: !b.is_active }),
    });
    load();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await authFetch(`/my/briefings/${deleteTarget.id}`, { method: "DELETE" });
    setDeleteTarget(null);
    load();
  };

  const addTopic = () => {
    const t = newTopic.trim();
    if (!t || !editing) return;
    setEditing({
      ...editing,
      query_topics: [...(editing.query_topics || []), t],
    });
    setNewTopic("");
  };

  const removeTopic = (idx) =>
    setEditing({
      ...editing,
      query_topics: editing.query_topics.filter((_, i) => i !== idx),
    });

  if (loading)
    return (
      <div className="flex items-center justify-center py-12 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading briefings…
      </div>
    );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-slate-900">
            Scheduled Briefings
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Automated KB digest delivered by email, Slack, or webhook.
          </p>
        </div>
        <button
          onClick={openNew}
          className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-1.5" /> New Briefing
        </button>
      </div>

      {briefings.length === 0 && !editing && (
        <div className="bg-white rounded-xl border border-slate-200 p-10 text-center text-slate-400">
          <Bell className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No briefings configured yet.</p>
        </div>
      )}

      {briefings.map((b) => (
        <div
          key={b.id}
          className="bg-white rounded-xl border border-slate-200 p-4 flex items-start justify-between"
        >
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="font-medium text-slate-900 text-sm">
                {b.briefing_name}
              </span>
              <span
                className={`text-xs px-2 py-0.5 rounded-full font-medium ${b.is_active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}
              >
                {b.is_active ? "Active" : "Paused"}
              </span>
            </div>
            <p className="text-xs text-slate-500">
              {SCHEDULE_OPTIONS.find((o) => o.value === b.schedule)?.label ||
                b.schedule}{" "}
              ·{" "}
              {DELIVERY_OPTIONS.find((o) => o.value === b.delivery_method)
                ?.label || b.delivery_method}{" "}
              → {b.delivery_destination}
            </p>
            {b.last_sent_at && (
              <p className="text-xs text-slate-400">
                Last sent:{" "}
                {new Date(b.last_sent_at).toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0 ml-4">
            <button
              onClick={() => toggleActive(b)}
              className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700"
              title={b.is_active ? "Pause" : "Activate"}
            >
              {b.is_active ? (
                <ToggleRight className="w-4 h-4 text-green-500" />
              ) : (
                <ToggleLeft className="w-4 h-4" />
              )}
            </button>
            <button
              onClick={() =>
                setEditing({ ...b, query_topics: b.query_topics || [] })
              }
              className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700"
              title="Edit"
            >
              <Edit3 className="w-4 h-4" />
            </button>
            <button
              onClick={() => setDeleteTarget(b)}
              className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-600"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}

      {/* Edit / Create form */}
      {editing && (
        <div className="bg-white rounded-xl border border-blue-200 p-5 space-y-4 shadow-sm">
          <h4 className="text-sm font-semibold text-slate-900">
            {editing.id ? "Edit Briefing" : "New Briefing"}
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-500 mb-1">
                Briefing Name *
              </label>
              <input
                type="text"
                value={editing.briefing_name}
                onChange={(e) =>
                  setEditing({ ...editing, briefing_name: e.target.value })
                }
                placeholder="Weekly Knowledge Digest"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">
                Schedule
              </label>
              <select
                value={editing.schedule}
                onChange={(e) =>
                  setEditing({ ...editing, schedule: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              >
                {SCHEDULE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">
                Delivery Method
              </label>
              <select
                value={editing.delivery_method}
                onChange={(e) =>
                  setEditing({ ...editing, delivery_method: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              >
                {DELIVERY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">
                {editing.delivery_method === "email"
                  ? "Email Address *"
                  : "Webhook URL *"}
              </label>
              <input
                type={editing.delivery_method === "email" ? "email" : "url"}
                value={editing.delivery_destination}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    delivery_destination: e.target.value,
                  })
                }
                placeholder={
                  editing.delivery_method === "email"
                    ? "team@example.com"
                    : "https://hooks.slack.com/…"
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-slate-500 mb-1">
              Query Topics{" "}
              <span className="text-slate-400">
                (briefing will cover these)
              </span>
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newTopic}
                onChange={(e) => setNewTopic(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addTopic()}
                placeholder="e.g. Upcoming events"
                className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
              />
              <button
                onClick={addTopic}
                className="px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {(editing.query_topics || []).map((t, i) => (
                <span
                  key={i}
                  className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs"
                >
                  {t}
                  <button onClick={() => removeTopic(i)}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs text-slate-500 mb-1">
              Custom Summary Prompt{" "}
              <span className="text-slate-400">(optional)</span>
            </label>
            <textarea
              value={editing.summary_prompt || ""}
              onChange={(e) =>
                setEditing({ ...editing, summary_prompt: e.target.value })
              }
              rows={3}
              placeholder="Summarize recent updates in a friendly tone, highlighting the top 3 items…"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm resize-none"
            />
          </div>

          <div className="flex items-center justify-between pt-2">
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={editing.is_active}
                onChange={(e) =>
                  setEditing({ ...editing, is_active: e.target.checked })
                }
                className="rounded"
              />
              Active
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => setEditing(null)}
                className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={
                  saving ||
                  !editing.briefing_name.trim() ||
                  !editing.delivery_destination.trim()
                }
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                ) : (
                  <Save className="w-4 h-4 mr-1.5" />
                )}
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-xl mx-4">
            <h4 className="font-semibold text-slate-900 mb-2">
              Delete Briefing?
            </h4>
            <p className="text-sm text-slate-500 mb-4">
              "{deleteTarget.briefing_name}" will be permanently removed.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// RAG CHATBOT — per facility
// ═══════════════════════════════════════════════════════════════════

export const KB_ROLE_LEVELS = {
  none: 0,
  viewer: 1,
  user: 1,
  editor: 2,
  approver: 3,
  admin: 4,
};

// ── KB Review Panel — pending_review items for editor+ ───────────

export const KB_TYPE_CONFIG = {
  kb_generated: {
    label: "KB Row",
    bg: "bg-violet-100",
    text: "text-violet-700",
    icon: Sparkles,
  },
  qa_pair: {
    label: "Q&A Pair",
    bg: "bg-emerald-100",
    text: "text-emerald-700",
    icon: MessageSquare,
  },
};

const KBReviewPanel = ({ facility, kbRole }) => {
  const kbRoleGte = (required) =>
    (KB_ROLE_LEVELS[kbRole] ?? 0) >= (KB_ROLE_LEVELS[required] ?? 99);
  const canApprove = kbRoleGte("approver");
  const canEdit = kbRoleGte("editor");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [reviewNotes, setReviewNotes] = useState({});
  const [processing, setProcessing] = useState(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [editMode, setEditMode] = useState(new Set());
  const [editDraft, setEditDraft] = useState({});
  const fid = facility.facility_id;

  const fetchItems = useCallback(async () => {
    setLoading(true);
    const data = await authFetch(
      `/rag/documents?facility_id=${encodeURIComponent(fid)}&status=pending_review`,
    );
    setItems(Array.isArray(data) ? data : []);
    setLoading(false);
  }, [fid]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleReview = async (id, action) => {
    setProcessing((prev) => new Set([...prev, id]));
    const result = await authFetch("/rag/documents/review", {
      method: "POST",
      body: JSON.stringify({ id, action, notes: reviewNotes[id] || "" }),
    });
    if (!result?.error) {
      setItems((prev) => prev.filter((item) => item.id !== id));
      setReviewNotes((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
    setProcessing((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleApproveAll = async () => {
    if (
      !window.confirm(
        `Approve all ${items.length} pending item(s)? They will be queued for embedding.`,
      )
    )
      return;
    setBulkProcessing(true);
    const result = await authFetch("/rag/documents/bulk-review", {
      method: "POST",
      body: JSON.stringify({ ids: items.map((i) => i.id), action: "approve" }),
    });
    if (!result?.error) setItems([]);
    setBulkProcessing(false);
  };

  const startEdit = (item) => {
    setEditDraft((prev) => ({
      ...prev,
      [item.id]: { title: item.title || "", content: item.content || "" },
    }));
    setEditMode((prev) => new Set([...prev, item.id]));
    setExpanded((prev) => new Set([...prev, item.id]));
  };

  const cancelEdit = (id) => {
    setEditMode((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setEditDraft((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleSaveEdit = async (id) => {
    const draft = editDraft[id];
    if (!draft) return;
    setProcessing((prev) => new Set([...prev, id]));
    const result = await authFetch("/rag/documents/update-content", {
      method: "POST",
      body: JSON.stringify({ id, title: draft.title, content: draft.content }),
    });
    if (!result?.error) {
      setItems((prev) =>
        prev.map((item) =>
          item.id === id
            ? { ...item, title: draft.title, content: draft.content }
            : item,
        ),
      );
      cancelEdit(id);
    }
    setProcessing((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const qaCount = items.filter((i) => i.source_type === "qa_pair").length;
  const kbCount = items.filter((i) => i.source_type !== "qa_pair").length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between bg-white rounded-lg shadow-sm border border-slate-200 p-3">
        <div className="flex items-center space-x-3">
          <ClipboardList className="w-5 h-5 text-violet-600" />
          <div>
            <h3 className="text-sm font-semibold text-slate-800">
              KB Review Queue
            </h3>
            <p className="text-xs text-slate-500">
              {items.length === 0
                ? "No items pending review"
                : `${items.length} item${items.length !== 1 ? "s" : ""} awaiting review`}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {items.length > 0 && (
            <>
              {kbCount > 0 && (
                <span className="px-2 py-1 bg-violet-100 text-violet-700 rounded-full text-xs font-medium">
                  {kbCount} KB row{kbCount !== 1 ? "s" : ""}
                </span>
              )}
              {qaCount > 0 && (
                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                  {qaCount} Q&amp;A pair{qaCount !== 1 ? "s" : ""}
                </span>
              )}
              {canApprove && (
                <button
                  onClick={handleApproveAll}
                  disabled={bulkProcessing}
                  className="flex items-center px-3 py-1.5 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {bulkProcessing ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                  ) : (
                    <Check className="w-3.5 h-3.5 mr-1" />
                  )}
                  Approve All
                </button>
              )}
            </>
          )}
          <button
            onClick={fetchItems}
            className="p-1.5 text-slate-500 hover:text-slate-700"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          Loading review queue...
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-xl">
          <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-400" />
          <p className="font-medium text-slate-700">Queue is empty</p>
          <p className="text-sm text-slate-400 mt-1">
            No KB rows or Q&amp;A pairs are waiting for review.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => {
            const typeKey =
              item.source_type === "qa_pair" ? "qa_pair" : "kb_generated";
            const tc = KB_TYPE_CONFIG[typeKey];
            const TypeIcon = tc.icon;
            const meta = item.metadata || {};
            const isOpen = expanded.has(item.id);
            const isProcessing = processing.has(item.id);
            const isEditing = editMode.has(item.id);
            const draft = editDraft[item.id] || {};

            return (
              <div
                key={item.id}
                className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
              >
                {/* Collapsed header — always visible */}
                <button
                  onClick={() => toggleExpand(item.id)}
                  className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center space-x-3 min-w-0">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${tc.bg} ${tc.text}`}
                    >
                      <TypeIcon className="w-3 h-3 mr-1" />
                      {tc.label}
                    </span>
                    {item.is_master && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 bg-amber-100 text-amber-700">
                        <Star className="w-3 h-3 mr-1" />
                        Master Summary
                      </span>
                    )}
                    {item.chunk_total > 1 && (
                      <span className="text-xs text-slate-400 flex-shrink-0">
                        {item.chunk_index + 1}/{item.chunk_total}
                      </span>
                    )}
                    <span className="text-sm font-medium text-slate-800 truncate">
                      {item.title || item.source_filename || `Item #${item.id}`}
                    </span>
                    {!isOpen && item.content && (
                      <span className="text-xs text-slate-400 truncate hidden sm:block">
                        — {item.content.substring(0, 80)}
                        {item.content.length > 80 ? "…" : ""}
                      </span>
                    )}
                  </div>
                  <ChevronDown
                    className={`w-4 h-4 text-slate-400 flex-shrink-0 ml-2 transition-transform ${isOpen ? "rotate-180" : ""}`}
                  />
                </button>

                {/* Expanded detail */}
                {isOpen && (
                  <div className="border-t border-slate-100">
                    {/* Title + content — editable when in edit mode */}
                    <div className="px-4 py-3 space-y-3">
                      {isEditing ? (
                        <>
                          <div>
                            <label className="text-xs font-medium text-slate-500 uppercase mb-1 block">
                              Title
                            </label>
                            <input
                              type="text"
                              value={draft.title}
                              onChange={(e) =>
                                setEditDraft((prev) => ({
                                  ...prev,
                                  [item.id]: {
                                    ...prev[item.id],
                                    title: e.target.value,
                                  },
                                }))
                              }
                              className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                              placeholder="Title…"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-slate-500 uppercase mb-1 block">
                              Content
                            </label>
                            <textarea
                              value={draft.content}
                              onChange={(e) =>
                                setEditDraft((prev) => ({
                                  ...prev,
                                  [item.id]: {
                                    ...prev[item.id],
                                    content: e.target.value,
                                  },
                                }))
                              }
                              rows={8}
                              className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 resize-y"
                              placeholder="Content…"
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <h4 className="text-xs font-medium text-slate-500 uppercase mb-1.5">
                              Content
                            </h4>
                            <div className="bg-slate-50 rounded-lg p-3 max-h-52 overflow-y-auto">
                              <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                                {item.content || "No content"}
                              </p>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Source + metadata */}
                    <div className="px-4 py-2 border-t border-slate-100 bg-slate-50/60">
                      <div className="flex flex-wrap items-center gap-2">
                        {item.source_filename && (
                          <span className="flex items-center text-xs text-slate-500">
                            <FileText className="w-3 h-3 mr-1 flex-shrink-0" />
                            <span className="truncate max-w-[180px]">
                              {item.source_filename}
                            </span>
                          </span>
                        )}
                        {item.ai_enriched && (
                          <span className="flex items-center px-1.5 py-0.5 text-xs bg-violet-100 text-violet-700 rounded font-medium">
                            <Sparkles className="w-3 h-3 mr-1" />
                            AI
                          </span>
                        )}
                        {Object.entries(meta)
                          .filter(
                            ([k, v]) =>
                              k !== "doc_type" || (v && v !== "qa_pair"),
                          )
                          .filter(
                            ([, v]) =>
                              v !== null &&
                              v !== undefined &&
                              String(v).trim() !== "",
                          )
                          .map(([k, v]) => (
                            <span
                              key={k}
                              className="px-1.5 py-0.5 text-xs bg-slate-100 text-slate-600 rounded"
                              title={k}
                            >
                              <span className="text-slate-400">{k}:</span>{" "}
                              {String(v)}
                            </span>
                          ))}
                        {item.pinecone_namespace && (
                          <span className="flex items-center text-xs text-slate-400 ml-auto">
                            <Database className="w-3 h-3 mr-1" />
                            {item.pinecone_namespace}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Review actions */}
                    {(canApprove || canEdit) && (
                      <div className="px-4 py-3 border-t border-slate-100 flex items-center gap-2 flex-wrap">
                        {isEditing ? (
                          <>
                            <span className="text-xs text-slate-500 flex-1">
                              Editing — save to preserve changes before
                              approving.
                            </span>
                            <button
                              onClick={() => handleSaveEdit(item.id)}
                              disabled={isProcessing}
                              className="flex items-center px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
                            >
                              {isProcessing ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Check className="w-3 h-3 mr-1" />
                              )}
                              Save
                            </button>
                            <button
                              onClick={() => cancelEdit(item.id)}
                              disabled={isProcessing}
                              className="flex items-center px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg disabled:opacity-50"
                            >
                              <X className="w-3 h-3 mr-1" /> Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            {canApprove && (
                              <input
                                type="text"
                                value={reviewNotes[item.id] || ""}
                                onChange={(e) =>
                                  setReviewNotes((prev) => ({
                                    ...prev,
                                    [item.id]: e.target.value,
                                  }))
                                }
                                placeholder="Optional notes..."
                                className="flex-1 min-w-0 px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500"
                              />
                            )}
                            {canEdit && (
                              <button
                                onClick={() => startEdit(item)}
                                disabled={isProcessing}
                                className="flex items-center px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg disabled:opacity-50"
                              >
                                <Pencil className="w-3 h-3 mr-1" /> Edit
                              </button>
                            )}
                            {canApprove && (
                              <>
                                <button
                                  onClick={() =>
                                    handleReview(item.id, "approve")
                                  }
                                  disabled={isProcessing}
                                  className="flex items-center px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50"
                                >
                                  {isProcessing ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <Check className="w-3 h-3 mr-1" />
                                  )}
                                  Approve
                                </button>
                                <button
                                  onClick={() => handleReview(item.id, "deny")}
                                  disabled={isProcessing}
                                  className="flex items-center px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
                                >
                                  <X className="w-3 h-3 mr-1" /> Deny
                                </button>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── KB Panel — sub-tabs: Documents | KB Review | Gap Tasks ────────

const VectorSearch = ({ facility, kbRole }) => {
  const kbRoleGte = (required) =>
    (KB_ROLE_LEVELS[kbRole] ?? 0) >= (KB_ROLE_LEVELS[required] ?? 99);
  const canDelete = kbRoleGte("admin");
  const fid = facility.facility_id;

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [confirmId, setConfirmId] = useState(null);
  const [removing, setRemoving] = useState(null);

  const handleSearch = async () => {
    if (!query.trim() || searching) return;
    setSearching(true);
    setSearchError("");
    setResults([]);
    setSearched(false);
    const qs = new URLSearchParams({
      facility_id: fid,
      q: query.trim(),
      limit: 25,
    });
    const data = await authFetch(`/rag/kb-search?${qs}`);
    setSearching(false);
    setSearched(true);
    if (data.error) {
      setSearchError(data.error);
    } else {
      setResults(Array.isArray(data) ? data : []);
    }
  };

  const handleRemove = async (result) => {
    setRemoving(result.doc_id);
    const data = await authFetch("/rag/vector-remove", {
      method: "POST",
      body: JSON.stringify({ facility_id: fid, doc_id: result.doc_id }),
    });
    setRemoving(null);
    setConfirmId(null);
    if (!data.error) {
      setResults((prev) => prev.filter((r) => r.doc_id !== result.doc_id));
    }
  };

  return (
    <div className="space-y-4">
      {/* Search bar */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
        <p className="text-xs text-slate-500 mb-3">
          Search the knowledge base by keyword to find and remove specific
          content. Documents are reset to draft so they can be corrected and
          re-approved. Q&amp;A pairs are deleted permanently.
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="e.g. early voting hours, endorsement policy…"
            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            onClick={handleSearch}
            disabled={!query.trim() || searching}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {searching ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Search className="w-4 h-4" />
            )}
            Search
          </button>
        </div>
        {searchError && (
          <p className="text-xs text-red-600 mt-2">{searchError}</p>
        )}
      </div>

      {/* Results */}
      {results.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-slate-500 px-1">
            {results.length} result{results.length !== 1 ? "s" : ""}
            {!canDelete && (
              <span className="ml-1 text-slate-400">
                · KB admin role required to remove
              </span>
            )}
          </p>
          {results.map((r) => {
            const isQA = r.source_type === "qa_pair";
            const isConfirming = confirmId === r.doc_id;
            const isRemoving = removing === r.doc_id;

            return (
              <div
                key={r.doc_id}
                className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          isQA
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-blue-100 text-blue-700"
                        }`}
                      >
                        {isQA ? "Q&A" : "Document"}
                      </span>
                      {r.doc_status && (
                        <span className="text-[10px] text-slate-400 capitalize">
                          {r.doc_status}
                        </span>
                      )}
                      {r.matching_chunks != null && (
                        <span className="text-[10px] text-slate-400">
                          · {r.matching_chunks} of {r.chunk_total} chunks match
                        </span>
                      )}
                    </div>
                    {r.title && (
                      <p className="text-sm font-medium text-slate-800 mb-1 truncate">
                        {r.title}
                      </p>
                    )}
                    <p className="text-xs text-slate-500 leading-relaxed line-clamp-3">
                      {r.content_preview || (
                        <span className="italic text-slate-400">
                          No content preview
                        </span>
                      )}
                    </p>
                  </div>

                  {canDelete && (
                    <div className="flex-shrink-0 mt-0.5">
                      {!isConfirming ? (
                        <button
                          onClick={() => setConfirmId(r.doc_id)}
                          className="px-2.5 py-1.5 text-xs text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors whitespace-nowrap"
                        >
                          {isQA ? "Delete Q&A" : "Remove from KB"}
                        </button>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-slate-500">Sure?</span>
                          <button
                            onClick={() => handleRemove(r)}
                            disabled={isRemoving}
                            className="px-2.5 py-1.5 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-1 whitespace-nowrap"
                          >
                            {isRemoving && (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            )}
                            {isQA ? "Delete" : "Remove"}
                          </button>
                          <button
                            onClick={() => setConfirmId(null)}
                            className="text-xs text-slate-400 hover:text-slate-600 px-1"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {searched && results.length === 0 && !searchError && (
        <div className="text-center text-slate-400 text-sm py-8">
          No matching content found.
        </div>
      )}
    </div>
  );
};

// ── Response Feedback — thumbs up/down with the Q&A that drew them ──
const FEEDBACK_PAGE = 50;

const FEEDBACK_COLUMNS = [
  { id: "created_at", label: "When", cls: "w-32" },
  { id: "rating", label: "Feedback", cls: "w-52" },
  { id: "question", label: "Question & response" },
  { id: "similarity", label: "Match", cls: "w-20" },
];

const ResponseFeedbackPanel = ({ facility }) => {
  const fid = facility.facility_id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rating, setRating] = useState("all");
  const [commentsOnly, setCommentsOnly] = useState(false);
  const [search, setSearch] = useState("");
  // Debounced separately so typing does not fire a request per keystroke.
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("created_at");
  const [dir, setDir] = useState("desc");
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState(() => new Set());

  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Any change to the result set sends you back to the first page — page 3 of
  // a 1-page result reads as "no feedback".
  useEffect(() => {
    setOffset(0);
  }, [rating, commentsOnly, query, sort, dir]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const params = new URLSearchParams({
      facility_id: fid,
      rating,
      sort,
      dir,
      limit: String(FEEDBACK_PAGE),
      offset: String(offset),
    });
    if (commentsOnly) params.set("comments_only", "true");
    if (query) params.set("q", query);
    const res = await authFetch(`/rag/feedback?${params}`);
    if (res?.error) setError(res.error);
    else setData(res);
    setLoading(false);
  }, [fid, rating, commentsOnly, query, sort, dir, offset]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleSort = (id) => {
    if (sort === id) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSort(id);
      setDir("desc");
    }
  };

  const toggleRow = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const items = data?.items || [];
  const total = data?.total ?? 0;
  const shownFrom = total === 0 ? 0 : offset + 1;
  const shownTo = Math.min(offset + FEEDBACK_PAGE, total);

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-3 flex flex-wrap items-center gap-2">
        <select
          value={rating}
          onChange={(e) => setRating(e.target.value)}
          className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm"
        >
          <option value="all">All ratings</option>
          <option value="down">Thumbs down</option>
          <option value="up">Thumbs up</option>
        </select>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search question, response or comment"
          className="flex-1 min-w-[220px] px-3 py-1.5 border border-slate-300 rounded-lg text-sm"
        />
        <label className="flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={commentsOnly}
            onChange={(e) => setCommentsOnly(e.target.checked)}
            className="rounded"
          />
          With a comment
        </label>
        <button
          onClick={load}
          disabled={loading}
          className="p-1.5 text-slate-400 hover:text-slate-600 rounded"
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
        {data && (
          <span className="flex items-center gap-3 text-xs text-slate-500">
            <span className="flex items-center gap-1 text-emerald-600">
              <ThumbsUp className="w-3.5 h-3.5" />
              {data.totals.up}
            </span>
            <span className="flex items-center gap-1 text-red-600">
              <ThumbsDown className="w-3.5 h-3.5" />
              {data.totals.down}
            </span>
          </span>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <table className="w-full text-sm table-fixed">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {FEEDBACK_COLUMNS.map((c) => (
                <th
                  key={c.id}
                  onClick={() => toggleSort(c.id)}
                  className={`px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide cursor-pointer select-none hover:text-slate-700 ${c.cls || ""}`}
                >
                  {c.label}
                  {sort === c.id &&
                    (dir === "asc" ? (
                      <ChevronUp className="inline w-3 h-3 ml-1" />
                    ) : (
                      <ChevronDown className="inline w-3 h-3 ml-1" />
                    ))}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && items.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-slate-400">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-slate-400">
                  No feedback matches these filters.
                </td>
              </tr>
            )}
            {items.map((f) => {
              const open = expanded.has(f.id);
              const down = f.rating === -1;
              return (
                <tr
                  key={f.id}
                  onClick={() => toggleRow(f.id)}
                  className="hover:bg-slate-50 cursor-pointer align-top"
                >
                  <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                    {f.created_at
                      ? new Date(f.created_at).toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                        })
                      : "—"}
                    {/* Only in-app surfaces have a rater — the embed widget is
                        anonymous, so most rows show nothing here. */}
                    {f.rated_by && (
                      <span
                        className="block text-slate-600 truncate"
                        title={f.rated_by}
                      >
                        {f.rated_by}
                      </span>
                    )}
                    {f.channel && (
                      <span className="block text-slate-400">{f.channel}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                        down
                          ? "bg-red-50 text-red-700"
                          : "bg-emerald-50 text-emerald-700"
                      }`}
                    >
                      {down ? (
                        <ThumbsDown className="w-3 h-3" />
                      ) : (
                        <ThumbsUp className="w-3 h-3" />
                      )}
                      {down ? "Down" : "Up"}
                    </span>
                    {f.user_comment && (
                      <p
                        className={`mt-1.5 text-xs text-slate-600 italic ${open ? "" : "line-clamp-3"}`}
                      >
                        “{f.user_comment}”
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {f.user_question ? (
                      <>
                        <p
                          className={`text-slate-800 ${open ? "" : "line-clamp-2"}`}
                        >
                          {f.user_question}
                        </p>
                        <p
                          className={`mt-1 text-xs text-slate-500 ${open ? "whitespace-pre-wrap" : "line-clamp-2"}`}
                        >
                          {f.ai_response || "No response recorded."}
                        </p>
                      </>
                    ) : (
                      /* conversation_log_id is nullable — the rating still
                         counts, we just cannot show what drew it. */
                      <p className="text-xs text-slate-400 italic">
                        The conversation behind this rating was not recorded.
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {typeof f.similarity_score === "number"
                      ? f.similarity_score.toFixed(2)
                      : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {total > 0 && (
          <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-t border-slate-200 text-xs text-slate-500">
            <span>
              {shownFrom}–{shownTo} of {total}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setOffset((o) => Math.max(0, o - FEEDBACK_PAGE))}
                disabled={offset === 0}
                className="px-2 py-1 rounded border border-slate-300 bg-white disabled:opacity-40"
              >
                Previous
              </button>
              <button
                onClick={() => setOffset((o) => o + FEEDBACK_PAGE)}
                disabled={shownTo >= total}
                className="px-2 py-1 rounded border border-slate-300 bg-white disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const KBPanel = ({ facility, user, kbRole }) => {
  const kbRoleGte = (required) =>
    (KB_ROLE_LEVELS[kbRole] ?? 0) >= (KB_ROLE_LEVELS[required] ?? 99);
  const canEdit = kbRoleGte("editor");
  const canApproveKB = kbRoleGte("approver");
  const [subTab, setSubTab] = useState("documents");

  const subTabs = [
    { id: "documents", label: "Documents", icon: FileText },
    ...(canEdit
      ? [{ id: "kb-review", label: "KB Review", icon: ClipboardList }]
      : []),
    ...(canApproveKB
      ? [{ id: "gap-tasks", label: "Gap Tasks", icon: Brain }]
      : []),
    ...(canEdit
      ? [
          {
            id: "response-feedback",
            label: "Response Feedback",
            icon: ThumbsUp,
          },
        ]
      : []),
    ...(canEdit
      ? [{ id: "vector-search", label: "Vector Search", icon: Search }]
      : []),
  ];

  return (
    <div className="space-y-4">
      {/* Sub-tab bar */}
      <div className="flex space-x-1 bg-slate-100 rounded-lg p-1">
        {subTabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              subTab === t.id
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            <t.icon className="w-4 h-4 mr-1.5" />
            {t.label}
          </button>
        ))}
      </div>

      {subTab === "documents" && (
        <RAGDocuments facility={facility} user={user} kbRole={kbRole} />
      )}
      {subTab === "kb-review" && canEdit && (
        <KBReviewPanel facility={facility} kbRole={kbRole} />
      )}
      {subTab === "gap-tasks" && canApproveKB && (
        <GapTasksPanel facility={facility} user={user} />
      )}
      {subTab === "response-feedback" && canEdit && (
        <ResponseFeedbackPanel facility={facility} />
      )}
      {subTab === "vector-search" && canEdit && (
        <VectorSearch facility={facility} kbRole={kbRole} />
      )}
    </div>
  );
};

// ── Chat Panel — sub-tabs: Chat | Customize ─────────────────────
const ChatPanel = ({ facility, user, kbRole }) => {
  const kbRoleGte = (required) =>
    (KB_ROLE_LEVELS[kbRole] ?? 0) >= (KB_ROLE_LEVELS[required] ?? 99);
  const isAdmin = kbRoleGte("admin");
  const [subTab, setSubTab] = useState("chat");

  const subTabs = [
    { id: "chat", label: "Echo", icon: Bot },
    ...(isAdmin
      ? [{ id: "customize", label: "Customize", icon: Palette }]
      : []),
  ];

  return (
    <div className="space-y-4">
      <div className="flex space-x-1 bg-slate-100 rounded-lg p-1">
        {subTabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              subTab === t.id
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            <t.icon className="w-4 h-4 mr-1.5" />
            {t.label}
          </button>
        ))}
      </div>

      {subTab === "chat" && <RAGChatPanel facility={facility} user={user} />}
      {subTab === "customize" && isAdmin && (
        <RAGCustomize facility={facility} />
      )}
    </div>
  );
};

// ── VOIP Panel — sub-tabs: Live | Calls | KB Suggestions | Agent Config ──
export const RAGChat = ({
  facility,
  user,
  systemMessages = [],
  onDismissMessage,
}) => {
  const [tab, setTab] = useState("knowledge-base");
  const kbRole =
    facility.kb_role ||
    facility.services?.find((s) => s.service_type === "rag")?.access_role ||
    "viewer";
  const kbRoleGte = (required) =>
    (KB_ROLE_LEVELS[kbRole] ?? 0) >= (KB_ROLE_LEVELS[required] ?? 99);
  const isAdmin = kbRoleGte("admin");

  const tabs = [
    { id: "knowledge-base", label: "Knowledge Base", icon: Brain },
    { id: "chat", label: "Echo", icon: Bot },
    { id: "briefings", label: "Briefings", icon: Bell },
    ...(isAdmin
      ? [
          { id: "prompt-studio", label: "Prompt Studio", icon: Sliders },
          { id: "customize", label: "Customize", icon: Palette },
        ]
      : []),
  ];

  return (
    <div className="space-y-4">
      {/* Facility-scoped system message banners */}
      {systemMessages.map((msg) => (
        <SystemBanner key={msg.id} message={msg} onDismiss={onDismissMessage} />
      ))}

      {/* Tab bar */}
      <div className="flex space-x-1 bg-white rounded-lg shadow-sm border border-slate-200 p-1 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              tab === t.id
                ? "bg-blue-600 text-white"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <t.icon className="w-4 h-4 mr-1.5" />
            {t.label}
          </button>
        ))}
      </div>

      {tab === "knowledge-base" && (
        <KBPanel facility={facility} user={user} kbRole={kbRole} />
      )}
      {tab === "chat" && (
        <ChatPanel facility={facility} user={user} kbRole={kbRole} />
      )}
      {tab === "briefings" && <BriefingsPanel facility={facility} />}
      {tab === "prompt-studio" && isAdmin && (
        <RAGPromptStudio facility={facility} user={user} />
      )}
      {tab === "customize" && isAdmin && <RAGCustomize facility={facility} />}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// PAS PORTAL — Prior Authorization Support
// ═══════════════════════════════════════════════════════════════════

const STATUS_COLORS = {
  uploaded: "bg-slate-100 text-slate-700",
  parsing: "bg-yellow-100 text-yellow-800",
  parsed: "bg-blue-100 text-blue-800",
  approved: "bg-green-100 text-green-800",
  embedding: "bg-purple-100 text-purple-800",
  embedded: "bg-emerald-100 text-emerald-800",
  denied: "bg-red-100 text-red-800",
  failed: "bg-red-100 text-red-800",
  archived: "bg-slate-200 text-slate-500",
  metadata_pending: "bg-amber-100 text-amber-700",
  enriching: "bg-violet-100 text-violet-700",
  pending_review: "bg-amber-100 text-amber-800",
  kb_generated: "bg-violet-100 text-violet-800",
  kb_processed: "bg-teal-100 text-teal-800",
};

const QA_VERDICT = {
  good: { label: "Fit for retrieval", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  needs_work: { label: "Needs work", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  poor: { label: "Not ready", cls: "bg-red-50 text-red-700 border-red-200" },
};

const QA_SEVERITY = {
  high: "text-red-600",
  medium: "text-amber-600",
  low: "text-slate-500",
};

// Advisory grade on how well a draft Q&A will behave once retrieved. Never
// gates submission — an editor can disagree and submit anyway.
// Shared by the draft-entry modal and the per-document review in the
// Documents tab. The only difference is what Apply does: fill a form field,
// or write to rag_documents — hence applyLabel.
const QAReviewPanel = ({
  review,
  stale,
  running,
  onRun,
  onApply,
  applyLabel = "Apply",
}) => {
  const verdict = review && (QA_VERDICT[review.verdict] || QA_VERDICT.needs_work);
  const suggestions = review
    ? [
        ["question", "Suggested question", review.suggested_question],
        ["answer", "Suggested answer", review.suggested_answer],
      ].filter(([, , v]) => v)
    : [];

  return (
    <div className="border border-slate-200 rounded-lg">
      <div className="flex items-center justify-between px-3 py-2">
        <span className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
          <Sparkles className="w-3.5 h-3.5 text-violet-500" />
          AI Review
        </span>
        <button
          type="button"
          onClick={onRun}
          disabled={running}
          className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded-md hover:bg-violet-100 disabled:opacity-50"
        >
          {running && <Loader2 className="w-3 h-3 animate-spin" />}
          {running ? "Reviewing…" : review ? "Re-run" : "Review entry"}
        </button>
      </div>

      {!review && !running && (
        <p className="px-3 pb-2.5 text-xs text-slate-400">
          Checks phrasing, length, self-containment and overlap with entries you
          already have.
        </p>
      )}

      {review && (
        <div className={`px-3 pb-3 space-y-3 ${stale ? "opacity-50" : ""}`}>
          {stale && (
            <p className="text-xs text-amber-600">
              Edited since this review — re-run to grade the current text.
            </p>
          )}

          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 text-xs font-medium rounded border ${verdict.cls}`}>
              {verdict.label}
            </span>
            <span className="text-xs text-slate-400">{review.score}/100</span>
          </div>

          {review.summary && (
            <p className="text-xs text-slate-600 leading-relaxed">{review.summary}</p>
          )}

          {review.issues?.length > 0 && (
            <ul className="space-y-1.5">
              {review.issues.map((it, i) => (
                <li key={i} className="flex gap-1.5 text-xs">
                  <AlertTriangle
                    className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${QA_SEVERITY[it.severity]}`}
                  />
                  <span className="text-slate-600">
                    <span className="capitalize text-slate-400">{it.field}</span>
                    {" · "}
                    {it.issue}
                    {it.suggestion && (
                      <span className="text-slate-500"> — {it.suggestion}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {suggestions.map(([field, label, value]) => (
            <div key={field} className="bg-slate-50 border border-slate-200 rounded-md p-2">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-xs font-medium text-slate-500">{label}</span>
                <button
                  type="button"
                  onClick={() => onApply(field)}
                  disabled={running}
                  className="text-xs font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50"
                >
                  {applyLabel}
                </button>
              </div>
              <p className="text-xs text-slate-700 whitespace-pre-wrap">{value}</p>
            </div>
          ))}

          {review.similar?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-slate-500 mb-1">
                Similar entries already in the KB
              </p>
              <ul className="space-y-1">
                {review.similar.map((m) => (
                  <li key={m.id} className="flex items-start gap-2 text-xs text-slate-600">
                    <span className="text-slate-400 flex-shrink-0">
                      {Math.round(m.overlap * 100)}%
                    </span>
                    <span>{m.question}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Say what was actually compared — "no similar entries" reads as a
              guarantee otherwise, and the scan is capped. */}
          {review.scanned >= review.scan_limit ? (
            <p className="text-xs text-slate-400">
              Compared against the {review.scan_limit} most recent Q&amp;A entries.
            </p>
          ) : (
            <p className="text-xs text-slate-400">
              Compared against {review.scanned} existing Q&amp;A{" "}
              {review.scanned === 1 ? "entry" : "entries"}.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const RAGDocuments = ({ facility, user, kbRole = "viewer" }) => {
  const kbRoleGte = (required) =>
    (KB_ROLE_LEVELS[kbRole] ?? 0) >= (KB_ROLE_LEVELS[required] ?? 99);
  const canEdit = kbRoleGte("editor");
  const canApprove = kbRoleGte("approver");
  const [docs, setDocs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [namespaceFilter, setNamespaceFilter] = useState("knowledge");
  const [uploading, setUploading] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState(new Set());
  const fileRef = useRef(null);
  const fid = facility.facility_id;
  const [qaModal, setQaModal] = useState(false);
  const [qaForm, setQaForm] = useState({ question: "", answer: "" });
  const [qaMetadata, setQaMetadata] = useState({});
  const [qaSchema, setQaSchema] = useState([]);
  const [qaSchemaLoading, setQaSchemaLoading] = useState(false);
  const [qaSubmitting, setQaSubmitting] = useState(false);
  const [qaError, setQaError] = useState("");
  // AI review of the draft entry. Advisory only — it never blocks submission.
  const [qaReview, setQaReview] = useState(null);
  const [qaReviewing, setQaReviewing] = useState(false);
  // Set once the text moves after a review, so a score cannot sit there
  // implying it graded whatever is currently in the box.
  const [qaReviewStale, setQaReviewStale] = useState(false);
  // AI review of entries already in the KB, keyed by document id. Same
  // endpoint and same panel as the draft review above — the difference is
  // that applying a rewrite here writes to rag_documents and re-queues the
  // document for embedding, rather than filling in a form field.
  const [docReviews, setDocReviews] = useState({});
  const [docReviewing, setDocReviewing] = useState(new Set());
  const [docApplying, setDocApplying] = useState(new Set());

  const editQaField = (field, value) => {
    setQaForm((f) => ({ ...f, [field]: value }));
    setQaReviewStale(true);
  };

  // Search + pagination
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  // Q&A row expand
  const [expandedDocs, setExpandedDocs] = useState({});
  const toggleDocExpand = (id) =>
    setExpandedDocs((s) => ({ ...s, [id]: !s[id] }));

  // Per-document image attachment
  const imgUploadRef = useRef(null);
  const [pendingImgDoc, setPendingImgDoc] = useState(null);
  const [docImgUploading, setDocImgUploading] = useState(new Set());
  const [docImgError, setDocImgError] = useState("");

  const handleDocImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !pendingImgDoc) return;
    const doc = pendingImgDoc;
    setPendingImgDoc(null);
    if (imgUploadRef.current) imgUploadRef.current.value = "";
    setDocImgUploading((s) => new Set([...s, doc.id]));
    setDocImgError("");
    const resized = await resizeImageToFile(file, {
      maxWidth: 800,
      maxHeight: 600,
    });
    const formData = new FormData();
    formData.append("file", resized);
    formData.append("facility_id", fid);
    const token = getToken();
    const uploadRes = await fetch(`${AUTH_URL}/upload/image`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    if (!uploadRes.ok) {
      setDocImgError("Image upload failed");
      setDocImgUploading((s) => {
        const n = new Set(s);
        n.delete(doc.id);
        return n;
      });
      return;
    }
    const uploadData = await uploadRes.json();
    const newMeta = { ...(doc.metadata || {}), image_url: uploadData.url };
    await authFetch("/rag/documents/update-metadata", {
      method: "POST",
      body: JSON.stringify({ id: doc.id, metadata: newMeta }),
    });
    setDocImgUploading((s) => {
      const n = new Set(s);
      n.delete(doc.id);
      return n;
    });
    fetchDocs();
  };

  const handleDocImageRemove = async (doc) => {
    const newMeta = { ...(doc.metadata || {}) };
    delete newMeta.image_url;
    await authFetch("/rag/documents/update-metadata", {
      method: "POST",
      body: JSON.stringify({ id: doc.id, metadata: newMeta }),
    });
    fetchDocs();
  };

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    let url = `/rag/documents?facility_id=${encodeURIComponent(fid)}&doc_type=${encodeURIComponent(namespaceFilter)}`;
    if (statusFilter) url += `&status=${encodeURIComponent(statusFilter)}`;
    const [docsData, statsData] = await Promise.all([
      authFetch(url),
      authFetch(`/rag/documents/stats?facility_id=${encodeURIComponent(fid)}`),
    ]);
    setDocs(Array.isArray(docsData) ? docsData : []);
    if (statsData && !statsData.error) setStats(statsData);
    setLoading(false);
  }, [fid, statusFilter, namespaceFilter]);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    const failed = [];
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("facility_id", fid);
      formData.append("doc_type", namespaceFilter);
      const token = getToken();
      try {
        const res = await fetch(`${AUTH_URL}/rag/documents/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        if (!res.ok) failed.push(file.name);
      } catch (err) {
        console.error("Upload failed:", err);
        failed.push(file.name);
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
    if (failed.length > 0) {
      showToast(
        `Failed to upload ${failed.length} file${failed.length > 1 ? "s" : ""}: ${failed.join(", ")}`,
      );
    } else if (files.length > 0) {
      showToast(
        `Uploaded ${files.length} file${files.length > 1 ? "s" : ""}.`,
        "success",
      );
    }
    fetchDocs();
  };

  const handleReview = async (docId, action) => {
    await authFetch("/rag/documents/review", {
      method: "POST",
      body: JSON.stringify({ id: docId, action }),
    });
    fetchDocs();
  };

  const handleBulkReview = async (action) => {
    if (selectedDocs.size === 0) return;
    if (
      !window.confirm(
        `${action === "approve" ? "Approve" : "Deny"} ${selectedDocs.size} document(s)?`,
      )
    )
      return;
    await authFetch("/rag/documents/bulk-review", {
      method: "POST",
      body: JSON.stringify({ ids: [...selectedDocs], action }),
    });
    setSelectedDocs(new Set());
    fetchDocs();
  };

  const handleArchive = async (docId, action) => {
    await authFetch("/rag/documents/archive", {
      method: "POST",
      body: JSON.stringify({ id: docId, action }),
    });
    fetchDocs();
  };

  const handleBulkArchive = async () => {
    if (selectedDocs.size === 0) return;
    if (
      !window.confirm(
        `Archive ${selectedDocs.size} document(s)? Embedded documents will be removed from the knowledge base.`,
      )
    )
      return;
    await authFetch("/rag/documents/bulk-archive", {
      method: "POST",
      body: JSON.stringify({ ids: [...selectedDocs], action: "archive" }),
    });
    setSelectedDocs(new Set());
    fetchDocs();
  };

  const loadQASchema = useCallback(async () => {
    if (!facility.vertical_id) return;
    setQaSchemaLoading(true);
    try {
      const params = new URLSearchParams({ vertical_id: facility.vertical_id });
      if (facility.facility_type)
        params.set("facility_type", facility.facility_type);
      const data = await authFetch(`/rag/metadata-schema?${params}`);
      setQaSchema(Array.isArray(data) ? data.filter((f) => f.is_active) : []);
    } catch {
      setQaSchema([]);
    } finally {
      setQaSchemaLoading(false);
    }
  }, [facility.vertical_id, facility.facility_type]);

  const openQAModal = useCallback(() => {
    setQaModal(true);
    setQaError("");
    setQaForm({ question: "", answer: "" });
    setQaMetadata({});
    setQaReview(null);
    setQaReviewStale(false);
    loadQASchema();
  }, [loadQASchema]);

  const handleAIReview = async () => {
    if (!qaForm.question.trim() || !qaForm.answer.trim()) {
      setQaError("Enter a question and answer before running a review.");
      return;
    }
    setQaReviewing(true);
    setQaError("");
    try {
      const result = await authFetch("/rag/qa-review", {
        method: "POST",
        body: JSON.stringify({
          facility_id: fid,
          question: qaForm.question,
          answer: qaForm.answer,
          doc_type: namespaceFilter,
        }),
      });
      if (result.error) {
        setQaError(result.error);
      } else {
        setQaReview(result);
        setQaReviewStale(false);
      }
    } catch {
      setQaError("Network error");
    }
    setQaReviewing(false);
  };

  // Applying a rewrite changes the text the score was based on, so the panel
  // goes stale here too — and the used suggestion is dropped so it cannot be
  // applied twice over its own output.
  const applyQaSuggestion = (field) => {
    const key = field === "question" ? "suggested_question" : "suggested_answer";
    const value = qaReview?.[key];
    if (!value) return;
    setQaForm((f) => ({ ...f, [field]: value }));
    setQaReview((r) => (r ? { ...r, [key]: null } : r));
    setQaReviewStale(true);
  };

  // ── AI review of an entry already in the KB ────────────────────────────────

  const runDocReview = async (doc) => {
    setDocReviewing((s) => new Set([...s, doc.id]));
    try {
      const result = await authFetch("/rag/qa-review", {
        method: "POST",
        body: JSON.stringify({
          facility_id: fid,
          question: doc.title || "",
          answer: doc.content || "",
          doc_type: doc.doc_type || namespaceFilter,
        }),
      });
      setDocReviews((r) => ({
        ...r,
        [doc.id]: result?.error ? { error: result.error } : { ...result, stale: false },
      }));
    } catch {
      setDocReviews((r) => ({ ...r, [doc.id]: { error: "Network error" } }));
    }
    setDocReviewing((s) => {
      const next = new Set(s);
      next.delete(doc.id);
      return next;
    });
  };

  // Writes the rewrite straight to the document. The backend drops the old
  // Pinecone vector and puts the row back in the 'approved' queue, so the KB
  // stops answering from the previous wording once the pipeline runs again.
  const applyDocSuggestion = async (doc, field) => {
    const review = docReviews[doc.id];
    const key = field === "question" ? "suggested_question" : "suggested_answer";
    const value = review?.[key];
    if (!value) return;

    setDocApplying((s) => new Set([...s, doc.id]));
    try {
      const payload =
        field === "question" ? { id: doc.id, title: value } : { id: doc.id, content: value };
      const result = await authFetch("/rag/documents/update-content", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (result?.error) {
        setDocReviews((r) => ({ ...r, [doc.id]: { ...r[doc.id], error: result.error } }));
      } else {
        setDocs((prev) =>
          prev.map((d) =>
            d.id === doc.id
              ? {
                  ...d,
                  ...(field === "question" ? { title: value } : { content: value }),
                  // Mirror the server: an edited embedded doc goes back to
                  // 'approved' until the pipeline re-embeds it.
                  ...(result?.requeued_for_embedding ? { status: "approved" } : {}),
                }
              : d,
          ),
        );
        // Drop the applied suggestion and mark the score stale — it graded
        // the text that has just been replaced.
        setDocReviews((r) => ({
          ...r,
          [doc.id]: { ...r[doc.id], [key]: null, stale: true, error: null },
        }));
      }
    } catch {
      setDocReviews((r) => ({ ...r, [doc.id]: { ...r[doc.id], error: "Network error" } }));
    }
    setDocApplying((s) => {
      const next = new Set(s);
      next.delete(doc.id);
      return next;
    });
  };

  const handleAddQA = async () => {
    if (!qaForm.question.trim() || !qaForm.answer.trim()) {
      setQaError("Question and answer are required.");
      return;
    }
    setQaSubmitting(true);
    setQaError("");
    // Collect metadata from schema-driven fields; stringify all values for backend
    const metadata = {};
    Object.entries(qaMetadata).forEach(([k, v]) => {
      if (v !== null && v !== undefined && String(v).trim() !== "") {
        metadata[k] = String(v);
      }
    });
    try {
      const result = await authFetch("/rag/qa-pair", {
        method: "POST",
        body: JSON.stringify({
          facility_id: fid,
          question: qaForm.question,
          answer: qaForm.answer,
          metadata,
          doc_type: namespaceFilter,
        }),
      });
      if (result.success) {
        setQaModal(false);
        setQaForm({ question: "", answer: "" });
        setQaMetadata({});
        fetchDocs();
      } else {
        setQaError(result.error || "Save failed");
      }
    } catch (err) {
      setQaError("Network error");
    }
    setQaSubmitting(false);
  };

  const toggleSelect = (id) => {
    setSelectedDocs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedDocs.size === pagedDocs.length) setSelectedDocs(new Set());
    else setSelectedDocs(new Set(pagedDocs.map((d) => d.id)));
  };

  const totalDocs = Object.values(stats).reduce(
    (a, s) => a + (s.count || 0),
    0,
  );

  const filteredDocs = docs.filter((doc) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (doc.title || doc.filename || "").toLowerCase().includes(q) ||
      (doc.source_type || "").toLowerCase().includes(q)
    );
  });
  const totalPages = Math.ceil(filteredDocs.length / PAGE_SIZE);
  const pagedDocs = filteredDocs.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );

  useEffect(() => setPage(1), [searchQuery, statusFilter]);

  return (
    <div className="space-y-4">
      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-3">
          <p className="text-xs text-slate-500">Total</p>
          <p className="text-xl font-bold text-slate-900">{totalDocs}</p>
        </div>
        {["pending_review", "parsed", "approved", "embedded", "denied"].map(
          (s) => (
            <div
              key={s}
              className="bg-white rounded-lg shadow-sm border border-slate-200 p-3"
            >
              <p className="text-xs text-slate-500 capitalize">{s}</p>
              <p className="text-xl font-bold text-slate-900">
                {stats[s]?.count || 0}
              </p>
              {stats[s]?.chunks > 0 && (
                <p className="text-xs text-slate-400">
                  {stats[s].chunks} chunks
                </p>
              )}
            </div>
          ),
        )}
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between bg-white rounded-lg shadow-sm border border-slate-200 p-3 gap-2 flex-wrap">
        <div className="flex items-center space-x-2 flex-wrap gap-y-2">
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search documents…"
            className="px-3 py-1.5 border border-slate-300 rounded-lg text-xs w-48"
          />
          <select
            value={namespaceFilter}
            onChange={(e) => setNamespaceFilter(e.target.value)}
            className="px-3 py-1.5 border border-slate-300 rounded-lg text-xs font-medium"
          >
            <option value="knowledge">Knowledge Base</option>
            <option value="intent_example">Intent Examples</option>
            <option value="draft_template">Draft Templates</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-1.5 border border-slate-300 rounded-lg text-xs"
          >
            <option value="">All Statuses</option>
            {Object.keys(STATUS_COLORS).map((s) => (
              <option key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
          <button
            onClick={fetchDocs}
            className="p-1.5 text-slate-500 hover:text-slate-700"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          {selectedDocs.size > 0 && canApprove && (
            <div className="flex items-center space-x-2 ml-2 pl-2 border-l border-slate-200">
              <span className="text-xs text-slate-500">
                {selectedDocs.size} selected
              </span>
              <button
                onClick={() => handleBulkReview("approve")}
                className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
              >
                Approve
              </button>
              <button
                onClick={() => handleBulkReview("deny")}
                className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
              >
                Deny
              </button>
              <button
                onClick={handleBulkArchive}
                className="px-2 py-1 text-xs bg-slate-500 text-white rounded hover:bg-slate-600"
              >
                Archive
              </button>
            </div>
          )}
        </div>
        {canEdit && (
          <div className="flex items-center space-x-2">
            <input
              type="file"
              ref={fileRef}
              onChange={handleUpload}
              multiple
              accept=".pdf,.docx,.doc,.txt,.md,.csv"
              className="hidden"
            />
            <input
              type="file"
              ref={imgUploadRef}
              onChange={handleDocImageSelect}
              accept="image/*"
              className="hidden"
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-1.5" />
              )}
              {uploading ? "Uploading..." : "Upload Documents"}
            </button>
            <button
              onClick={openQAModal}
              className="flex items-center px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700"
            >
              <PlusCircle className="w-4 h-4 mr-1.5" />
              Add Q&amp;A
            </button>
          </div>
        )}
      </div>

      {docImgError && (
        <div className="mx-4 mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center justify-between">
          <span>{docImgError}</span>
          <button
            onClick={() => setDocImgError("")}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {qaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-800">
                Submit Q&amp;A for Review
              </h3>
              <button
                onClick={() => setQaModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Question *
                </label>
                <textarea
                  value={qaForm.question}
                  onChange={(e) => editQaField("question", e.target.value)}
                  rows={3}
                  placeholder="Enter the question..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Answer *
                </label>
                <textarea
                  value={qaForm.answer}
                  onChange={(e) => editQaField("answer", e.target.value)}
                  rows={5}
                  placeholder="Enter the answer..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              <QAReviewPanel
                review={qaReview}
                stale={qaReviewStale}
                running={qaReviewing}
                onRun={handleAIReview}
                onApply={applyQaSuggestion}
              />
              <div>
                <label className="flex items-center gap-1 text-xs font-medium text-slate-600 mb-2">
                  Metadata
                  {qaSchemaLoading && (
                    <Loader2 className="w-3 h-3 animate-spin text-slate-400" />
                  )}
                </label>
                {qaSchema.length > 0 ? (
                  <div className="grid grid-cols-2 gap-3">
                    {qaSchema.map((field) =>
                      field.field_type === "boolean" ? (
                        <div
                          key={field.field_name}
                          className="col-span-2 flex items-center gap-2"
                        >
                          <input
                            type="checkbox"
                            id={`qa-meta-${field.field_name}`}
                            checked={!!qaMetadata[field.field_name]}
                            onChange={(e) =>
                              setQaMetadata((m) => ({
                                ...m,
                                [field.field_name]: e.target.checked,
                              }))
                            }
                            className="rounded border-slate-300"
                          />
                          <label
                            htmlFor={`qa-meta-${field.field_name}`}
                            className="text-sm text-slate-700"
                          >
                            {field.description || field.field_name}
                            {field.required && (
                              <span className="text-red-500 ml-0.5">*</span>
                            )}
                          </label>
                        </div>
                      ) : (
                        <div key={field.field_name}>
                          <label className="block text-xs font-medium text-slate-600 mb-1">
                            {field.description || field.field_name}
                            {field.required && (
                              <span className="text-red-500 ml-0.5">*</span>
                            )}
                          </label>
                          {field.allowed_values?.length > 0 ? (
                            <select
                              value={qaMetadata[field.field_name] || ""}
                              onChange={(e) =>
                                setQaMetadata((m) => ({
                                  ...m,
                                  [field.field_name]: e.target.value,
                                }))
                              }
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Select...</option>
                              {field.allowed_values.map((v) => (
                                <option key={v} value={v}>
                                  {v}
                                </option>
                              ))}
                            </select>
                          ) : field.field_type === "integer" ? (
                            <input
                              type="number"
                              value={qaMetadata[field.field_name] || ""}
                              onChange={(e) =>
                                setQaMetadata((m) => ({
                                  ...m,
                                  [field.field_name]: e.target.value,
                                }))
                              }
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                            />
                          ) : (
                            <input
                              type="text"
                              value={qaMetadata[field.field_name] || ""}
                              onChange={(e) =>
                                setQaMetadata((m) => ({
                                  ...m,
                                  [field.field_name]: e.target.value,
                                }))
                              }
                              placeholder={
                                field.description || field.field_name
                              }
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                            />
                          )}
                        </div>
                      ),
                    )}
                  </div>
                ) : !qaSchemaLoading ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Category
                      </label>
                      <input
                        type="text"
                        value={qaMetadata.category || ""}
                        onChange={(e) =>
                          setQaMetadata((m) => ({
                            ...m,
                            category: e.target.value,
                          }))
                        }
                        placeholder="e.g. Policy, FAQ..."
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Topic
                      </label>
                      <input
                        type="text"
                        value={qaMetadata.topic || ""}
                        onChange={(e) =>
                          setQaMetadata((m) => ({
                            ...m,
                            topic: e.target.value,
                          }))
                        }
                        placeholder="e.g. Voting, Elections..."
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                ) : null}
              </div>
              {qaError && <p className="text-sm text-red-600">{qaError}</p>}
            </div>
            <div className="flex items-center justify-end space-x-3 px-5 py-4 border-t border-slate-200">
              <button
                onClick={() => setQaModal(false)}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
              >
                Cancel
              </button>
              <button
                onClick={handleAddQA}
                disabled={qaSubmitting}
                className="flex items-center px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                {qaSubmitting ? (
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-1.5" />
                )}
                {qaSubmitting ? "Submitting..." : "Submit for Review"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document table */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] table-fixed">
            <colgroup>
              {canApprove && <col className="w-8" />}
              <col />
              <col className="w-28" />
              <col className="w-24" />
              <col className="w-24" />
              {canApprove && <col className="w-24" />}
            </colgroup>
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {canApprove && (
                  <th className="px-2 py-2 w-8">
                    <input
                      type="checkbox"
                      checked={
                        pagedDocs.length > 0 &&
                        pagedDocs.every((d) => selectedDocs.has(d.id))
                      }
                      onChange={selectAll}
                      className="rounded"
                    />
                  </th>
                )}
                <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 uppercase">
                  Document
                </th>
                <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 uppercase">
                  Status
                </th>
                <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 uppercase">
                  Source
                </th>
                <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 uppercase">
                  Date
                </th>
                {canApprove && (
                  <th className="text-right px-3 py-2 text-xs font-medium text-slate-500 uppercase">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={canApprove ? 6 : 4}
                    className="px-3 py-8 text-center text-slate-400 text-xs"
                  >
                    Loading...
                  </td>
                </tr>
              ) : filteredDocs.length === 0 ? (
                <tr>
                  <td
                    colSpan={canApprove ? 6 : 4}
                    className="px-3 py-8 text-center text-slate-400 text-xs"
                  >
                    {searchQuery
                      ? "No documents match your search."
                      : statusFilter
                        ? "No documents with this status."
                        : "No documents yet. Upload files to get started."}
                  </td>
                </tr>
              ) : (
                pagedDocs.map((doc) => (
                  <React.Fragment key={doc.id}>
                    <tr
                      className={`border-b border-slate-100 hover:bg-slate-50 ${expandedDocs[doc.id] ? "bg-slate-50" : ""}`}
                    >
                      {canApprove && (
                        <td className="px-2 py-2">
                          <input
                            type="checkbox"
                            checked={selectedDocs.has(doc.id)}
                            onChange={() => toggleSelect(doc.id)}
                            className="rounded"
                          />
                        </td>
                      )}
                      <td className="px-3 py-2 min-w-0">
                        <div className="flex items-center gap-1.5">
                          {canEdit && (
                            <div className="flex-shrink-0 relative w-7 h-7">
                              {docImgUploading.has(doc.id) ? (
                                <div className="w-7 h-7 flex items-center justify-center border border-slate-200 rounded">
                                  <Loader2 className="w-3 h-3 animate-spin text-slate-400" />
                                </div>
                              ) : doc.metadata?.image_url ? (
                                <div className="relative group w-7 h-7">
                                  <img
                                    src={doc.metadata.image_url}
                                    alt=""
                                    className="w-7 h-7 object-cover rounded border border-slate-200"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleDocImageRemove(doc)}
                                    className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 text-white rounded-full hidden group-hover:flex items-center justify-center"
                                    title="Remove image"
                                  >
                                    <X className="w-2 h-2" />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setPendingImgDoc(doc);
                                    imgUploadRef.current?.click();
                                  }}
                                  className="w-7 h-7 border border-dashed border-slate-300 rounded flex items-center justify-center text-slate-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
                                  title="Add image"
                                >
                                  <ImagePlus className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          )}
                          {doc.content && (
                            <button
                              type="button"
                              onClick={() => toggleDocExpand(doc.id)}
                              className="flex-shrink-0 text-slate-400 hover:text-slate-600"
                              title={
                                expandedDocs[doc.id] ? "Collapse" : "Expand"
                              }
                            >
                              {expandedDocs[doc.id] ? (
                                <ChevronDown className="w-3 h-3" />
                              ) : (
                                <ChevronRight className="w-3 h-3" />
                              )}
                            </button>
                          )}
                          <div className="min-w-0">
                            <div className="text-xs font-medium text-slate-900 truncate">
                              {(() => {
                                if (doc.title) return doc.title;
                                const firstLine = (doc.content || "")
                                  .split("\n")[0]
                                  .trim();
                                if (firstLine.endsWith("?")) return firstLine;
                                if (doc.filename) return doc.filename;
                                if (firstLine)
                                  return firstLine.length > 80
                                    ? firstLine.slice(0, 80) + "…"
                                    : firstLine;
                                return `Doc #${doc.id}`;
                              })()}
                            </div>
                            {doc.file_size_bytes > 0 && (
                              <div className="text-[10px] text-slate-400">
                                {(doc.file_size_bytes / 1024).toFixed(1)} KB
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-medium ${STATUS_COLORS[doc.status] || "bg-slate-100 text-slate-600"}`}
                        >
                          {doc.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500 truncate">
                        {doc.source_type || "--"}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500">
                        {doc.created_at
                          ? new Date(doc.created_at).toLocaleDateString()
                          : "--"}
                      </td>
                      {canApprove && (
                        <td className="px-3 py-2 text-right">
                          <div className="flex justify-end space-x-0.5">
                            {doc.status === "archived" ? (
                              <button
                                type="button"
                                onClick={() =>
                                  handleArchive(doc.id, "unarchive")
                                }
                                className="p-1 text-slate-600 hover:bg-slate-100 rounded"
                                title="Restore"
                              >
                                <RotateCcw className="w-3.5 h-3.5" />
                              </button>
                            ) : (
                              <>
                                {(doc.status === "parsed" ||
                                  doc.status === "pending_review") && (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleReview(doc.id, "approve")
                                      }
                                      className="p-1 text-green-600 hover:bg-green-50 rounded"
                                      title="Approve"
                                    >
                                      <Check className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleReview(doc.id, "deny")
                                      }
                                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                                      title="Deny"
                                    >
                                      <X className="w-3.5 h-3.5" />
                                    </button>
                                  </>
                                )}
                                {doc.status === "denied" && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleReview(doc.id, "approve")
                                    }
                                    className="p-1 text-green-600 hover:bg-green-50 rounded"
                                    title="Re-approve"
                                  >
                                    <Check className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleArchive(doc.id, "archive")
                                  }
                                  className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
                                  title="Archive"
                                >
                                  <Archive className="w-3.5 h-3.5" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                    {expandedDocs[doc.id] && doc.content && (
                      <tr className="bg-blue-50 border-b border-slate-100">
                        <td colSpan={canApprove ? 6 : 4} className="px-4 py-3">
                          <div className="space-y-1.5 max-w-3xl">
                            {doc.source_type === "qa_pair" ? (
                              <>
                                <div>
                                  <div className="text-[10px] font-semibold text-blue-700 uppercase tracking-wide mb-0.5">
                                    Question
                                  </div>
                                  <p className="text-xs text-slate-800">
                                    {doc.title || "—"}
                                  </p>
                                </div>
                                <div>
                                  <div className="text-[10px] font-semibold text-blue-700 uppercase tracking-wide mb-0.5">
                                    Answer
                                  </div>
                                  <p className="text-xs text-slate-700 whitespace-pre-wrap">
                                    {doc.content || "—"}
                                  </p>
                                </div>
                              </>
                            ) : (
                              <>
                                {doc.title && (
                                  <div>
                                    <div className="text-[10px] font-semibold text-blue-700 uppercase tracking-wide mb-0.5">
                                      Title
                                    </div>
                                    <p className="text-xs text-slate-800">
                                      {doc.title}
                                    </p>
                                  </div>
                                )}
                                <div>
                                  <div className="text-[10px] font-semibold text-blue-700 uppercase tracking-wide mb-0.5">
                                    Content
                                  </div>
                                  <p className="text-xs text-slate-700 whitespace-pre-wrap">
                                    {doc.content}
                                  </p>
                                </div>
                              </>
                            )}

                            {/* AI review of this existing entry. Editor+ only,
                                because applying a suggestion writes to the
                                document and re-queues it for embedding. */}
                            {canEdit && !(doc.title || "").trim() ? (
                              // The reviewer grades a question against its
                              // answer, and /rag/qa-review rejects an empty
                              // question. Say why rather than let the admin
                              // click into a 400.
                              <p className="pt-2 text-xs text-slate-400">
                                AI review needs a title — this entry has none.
                                Add one and it can be reviewed.
                              </p>
                            ) : canEdit ? (
                              <div className="pt-2">
                                {docReviews[doc.id]?.error && (
                                  <p className="mb-1.5 text-xs text-red-600">
                                    {docReviews[doc.id].error}
                                  </p>
                                )}
                                <QAReviewPanel
                                  review={
                                    docReviews[doc.id]?.score !== undefined
                                      ? docReviews[doc.id]
                                      : null
                                  }
                                  stale={Boolean(docReviews[doc.id]?.stale)}
                                  running={
                                    docReviewing.has(doc.id) ||
                                    docApplying.has(doc.id)
                                  }
                                  onRun={() => runDocReview(doc)}
                                  onApply={(field) =>
                                    applyDocSuggestion(doc, field)
                                  }
                                  applyLabel="Apply & re-embed"
                                />
                                {docReviews[doc.id]?.stale && (
                                  <p className="mt-1 text-xs text-slate-400">
                                    Saved. The entry is queued for re-embedding —
                                    retrieval keeps using the old wording until
                                    the pipeline runs.
                                  </p>
                                )}
                              </div>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination footer */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-3 py-2 border-t border-slate-100 bg-slate-50 text-xs text-slate-500">
            <span>
              {(page - 1) * PAGE_SIZE + 1}–
              {Math.min(page * PAGE_SIZE, filteredDocs.length)} of{" "}
              {filteredDocs.length}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-2 py-1 rounded border border-slate-300 disabled:opacity-40 hover:bg-slate-100"
              >
                ‹
              </button>
              <span className="px-2">
                {page} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-2 py-1 rounded border border-slate-300 disabled:opacity-40 hover:bg-slate-100"
              >
                ›
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Facility User Management ──────────────────────────────────────

const FormatMsg = ({ text, accentColor }) => {
  const lines = text.split("\n");
  return lines.map((line, li) => (
    <span key={li}>
      {li > 0 && <br />}
      {parseMessageText(line).map((seg) =>
        seg.type === "link" ? (
          <a
            key={seg.key}
            href={seg.value}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: accentColor || "#6b8fc4",
              textDecoration: "underline",
            }}
          >
            {seg.value}
          </a>
        ) : (
          <span key={seg.key}>{seg.value}</span>
        ),
      )}
    </span>
  ));
};

const SourceCitations = ({ sources, colors, borderRadius }) => {
  const [expanded, setExpanded] = useState(false);
  if (!sources || sources.length === 0) return null;
  const br = Math.max(4, (borderRadius || 12) - 4);
  return (
    <div className="mt-2">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-xs font-medium transition-colors hover:opacity-80"
        style={{ color: colors.accent }}
      >
        <FileText className="w-3 h-3" />
        {sources.length} source{sources.length !== 1 ? "s" : ""}
        {expanded ? (
          <ChevronUp className="w-3 h-3" />
        ) : (
          <ChevronDown className="w-3 h-3" />
        )}
      </button>
      {expanded && (
        <div className="mt-2 space-y-1.5">
          {sources.map((src, i) => (
            <div
              key={i}
              className="px-3 py-2 text-xs"
              style={{
                backgroundColor: `${colors.accent}12`,
                border: `1px solid ${colors.accent}20`,
                borderRadius: `${br}px`,
                color: colors.text,
              }}
            >
              <div className="flex items-center justify-between mb-1">
                <span
                  className="font-medium"
                  style={{ color: colors.headings }}
                >
                  {src.name || src.source || `Document ${i + 1}`}
                </span>
                {src.score != null && (
                  <span
                    className="px-1.5 py-0.5 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor:
                        src.score > 0.8
                          ? "#22c55e20"
                          : src.score > 0.6
                            ? "#f59e0b20"
                            : "#ef444420",
                      color:
                        src.score > 0.8
                          ? "#22c55e"
                          : src.score > 0.6
                            ? "#f59e0b"
                            : "#ef4444",
                    }}
                  >
                    {(src.score * 100).toFixed(0)}% match
                  </span>
                )}
              </div>
              {src.page && (
                <span
                  className="text-xs mr-2"
                  style={{ color: colors.accent, opacity: 0.8 }}
                >
                  Page {src.page}
                </span>
              )}
              {src.snippet && (
                <p
                  className="leading-relaxed mt-1"
                  style={{ opacity: 0.7, lineHeight: "1.5" }}
                >
                  {src.snippet.length > 200
                    ? src.snippet.slice(0, 200) + "…"
                    : src.snippet}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const parseSources = (data) => {
  // Handle various response formats from n8n/RAG backends
  const raw =
    data.sourceDocuments ||
    data.sources ||
    data.documents ||
    data.context ||
    [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((doc) => ({
      name:
        doc.metadata?.source ||
        doc.metadata?.fileName ||
        doc.name ||
        doc.source ||
        "",
      page: doc.metadata?.page || doc.metadata?.loc?.pageNumber || doc.page,
      score: doc.score ?? doc.metadata?.score ?? null,
      snippet: doc.pageContent || doc.content || doc.text || doc.snippet || "",
    }))
    .slice(0, 10);
};

const RAGChatPanel = ({ facility, user }) => {
  const fid = facility.facility_id;
  const config = getRagConfig(fid);
  const colors = { ...DEFAULT_RAG_COLORS, ...config.colors };
  const topics =
    config.topics && Object.keys(config.topics).length
      ? config.topics
      : DEFAULT_QUICK_PROMPTS;
  const borderRadius = config.borderRadius || 12;
  const fontSize = config.fontSize || 13;
  const logoUrl = config.logoUrl || "";
  const botAvatarUrl = config.botAvatarUrl || "";
  const showSidebar = config.showSidebar !== false;
  const br = `${borderRadius}px`;
  const sbr = `${Math.max(4, borderRadius - 4)}px`;

  const welcomeMsg = {
    role: "assistant",
    content: `Welcome! I'm your ${facility.facility_name} knowledge base assistant, powered by deep-context RAG. My answers come from actual indexed documents.\n\nWhat would you like to know?`,
    timestamp: new Date().toISOString(),
  };

  // Conversation state
  const [conversations, setConversations] = useState(() =>
    loadConversations(fid, user.email),
  );
  const [activeConvId, setActiveConvId] = useState(() => {
    const all = loadConversations(fid, user.email);
    if (all.length > 0) return all[0].id;
    const fresh = createConversation(fid, user.email, welcomeMsg);
    return fresh.id;
  });
  const [messages, setMessages] = useState(() => {
    const all = loadConversations(fid, user.email);
    const conv = all.find((c) => c.id === activeConvId);
    return conv ? conv.messages : [welcomeMsg];
  });
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(showSidebar);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historySearch, setHistorySearch] = useState("");
  const [sessionId, setSessionId] = useState(
    () => `rag-${fid}-${user.email}-${Date.now()}`,
  );
  const endRef = useRef(null);
  const initials = getUserInitials(user);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Persist messages whenever they change
  useEffect(() => {
    if (!activeConvId || messages.length === 0) return;
    const title = autoTitle(messages);
    const updated = updateConversation(fid, user.email, activeConvId, {
      messages,
      title,
    });
    setConversations(updated);
  }, [messages, activeConvId, fid, user.email]);

  const switchConversation = (convId) => {
    const all = loadConversations(fid, user.email);
    const conv = all.find((c) => c.id === convId);
    if (!conv) return;
    setActiveConvId(convId);
    setMessages(conv.messages);
    setSessionId(`rag-${fid}-${user.email}-${Date.now()}`);
    setInput("");
    setHistoryOpen(false);
  };

  const startNewChat = () => {
    const conv = createConversation(fid, user.email, welcomeMsg);
    setConversations(loadConversations(fid, user.email));
    setActiveConvId(conv.id);
    setMessages([welcomeMsg]);
    setSessionId(`rag-${fid}-${user.email}-${Date.now()}`);
    setInput("");
    setHistoryOpen(false);
  };

  const removeConversation = (convId) => {
    const updated = deleteConversation(fid, user.email, convId);
    setConversations(updated);
    if (convId === activeConvId) {
      if (updated.length > 0) {
        switchConversation(updated[0].id);
      } else {
        startNewChat();
      }
    }
  };

  const handleFeedback = (msgIndex, feedback) => {
    setMessages((prev) => {
      const next = [...prev];
      next[msgIndex] = { ...next[msgIndex], feedback };
      return next;
    });
    trackEvent(fid, "feedback", { feedback, conversationId: activeConvId });
  };

  const send = async (text) => {
    const msg = text || input.trim();
    if (!msg || sending) return;
    const userMsg = {
      role: "user",
      content: msg,
      timestamp: new Date().toISOString(),
    };
    setMessages((p) => [...p, userMsg]);
    setInput("");
    setSending(true);
    trackEvent(fid, "query", { user: user.email });
    const sendStart = Date.now();
    try {
      const data = await authFetch("/rag/chat", {
        method: "POST",
        body: JSON.stringify({
          facility_id: fid,
          chatInput: msg,
          sessionId,
        }),
      });
      if (data.error) throw new Error(data.error);
      const sources = parseSources(data);
      const responseTime = Date.now() - sendStart;
      trackEvent(fid, "response", {
        responseTime,
        sourceCount: sources.length,
      });
      setMessages((p) => [
        ...p,
        {
          role: "assistant",
          content:
            data.output || data.text || data.response || JSON.stringify(data),
          timestamp: new Date().toISOString(),
          sources,
        },
      ]);
    } catch (err) {
      trackEvent(fid, "error", { message: err.message });
      setMessages((p) => [
        ...p,
        {
          role: "assistant",
          content: `Error: ${err.message}`,
          timestamp: new Date().toISOString(),
          isError: true,
        },
      ]);
    }
    setSending(false);
  };

  const filteredConversations = historySearch
    ? conversations.filter(
        (c) =>
          c.title.toLowerCase().includes(historySearch.toLowerCase()) ||
          c.messages.some(
            (m) =>
              m.role === "user" &&
              m.content.toLowerCase().includes(historySearch.toLowerCase()),
          ),
      )
    : conversations;

  const BotAvatar = () =>
    botAvatarUrl ? (
      <img
        src={botAvatarUrl}
        alt="AI"
        className="w-8 h-8 rounded-full object-cover flex-shrink-0"
        onError={(e) => {
          e.target.style.display = "none";
        }}
      />
    ) : (
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
        style={{ backgroundColor: colors.accent, color: "#fff" }}
      >
        AI
      </div>
    );

  return (
    <div
      className="flex h-[calc(100vh-240px)] shadow-md overflow-hidden"
      style={{
        backgroundColor: colors.bg,
        border: `1px solid ${colors.accent}33`,
        borderRadius: br,
      }}
    >
      {/* Sidebar with quick prompts */}
      {sidebarOpen && (
        <div
          className="w-72 flex-shrink-0 flex flex-col overflow-y-auto"
          style={{
            backgroundColor: colors.sidebar,
            borderRight: `1px solid ${colors.accent}33`,
          }}
        >
          <div
            className="px-4 py-3"
            style={{ borderBottom: `1px solid ${colors.accent}33` }}
          >
            <div className="flex items-center justify-between">
              <h3
                className="text-sm font-semibold"
                style={{ color: colors.headings }}
              >
                Quick Prompts
              </h3>
              <div className="flex items-center space-x-1">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span
                  className="text-xs"
                  style={{ color: colors.text, opacity: 0.6 }}
                >
                  RAG Active
                </span>
              </div>
            </div>
          </div>
          <div className="flex-1 px-3 py-3 space-y-4">
            {Object.entries(topics).map(([category, prompts]) => (
              <div key={category}>
                <h4
                  className="text-xs font-semibold uppercase tracking-wider mb-2 px-1"
                  style={{ color: colors.accent }}
                >
                  {category}
                </h4>
                <div className="space-y-1.5">
                  {prompts.map((prompt, i) => (
                    <button
                      key={i}
                      onClick={() => send(prompt)}
                      disabled={sending}
                      className="w-full text-left px-3 py-2 transition-all hover:opacity-80"
                      style={{
                        backgroundColor: `${colors.accent}20`,
                        color: colors.text,
                        border: `1px solid ${colors.accent}25`,
                        borderRadius: sbr,
                        fontSize: `${Math.max(10, fontSize - 2)}px`,
                      }}
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div
            className="px-4 py-3"
            style={{
              borderTop: `1px solid ${colors.accent}33`,
              backgroundColor: colors.bg,
            }}
          >
            <div
              className="text-xs font-semibold uppercase tracking-wider mb-1"
              style={{ color: colors.accent }}
            >
              <span className="w-2 h-2 rounded-full bg-green-500 inline-block mr-1.5 animate-pulse" />
              Deep Context RAG Active
            </div>
            <p
              className="text-xs leading-relaxed"
              style={{ color: colors.text, opacity: 0.6 }}
            >
              {facility.pinecone_index}/{facility.pinecone_namespace}
            </p>
          </div>
        </div>
      )}

      {/* Chat area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header bar */}
        <div
          className="px-4 py-3 flex items-center justify-between"
          style={{
            backgroundColor: colors.sidebar,
            borderBottom: `1px solid ${colors.accent}33`,
          }}
        >
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-1.5 transition-colors"
              style={{
                color: colors.accent,
                backgroundColor: `${colors.accent}15`,
                borderRadius: sbr,
              }}
            >
              <MessageSquare className="w-4 h-4" />
            </button>
            {logoUrl && (
              <img
                src={logoUrl}
                alt=""
                className="h-7 object-contain"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            )}
            <div>
              <h3
                className="font-semibold"
                style={{ color: colors.headings, fontSize: `${fontSize}px` }}
              >
                {facility.facility_name}
              </h3>
              <p
                style={{
                  color: colors.text,
                  opacity: 0.6,
                  fontSize: `${Math.max(10, fontSize - 2)}px`,
                }}
              >
                Knowledge Base Chat
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setHistoryOpen(!historyOpen)}
              className="p-1.5 transition-colors"
              style={{
                color: historyOpen ? "#fff" : colors.accent,
                backgroundColor: historyOpen
                  ? colors.accent
                  : `${colors.accent}15`,
                borderRadius: sbr,
              }}
              title="Conversation history"
            >
              <History className="w-4 h-4" />
            </button>
            <button
              onClick={startNewChat}
              className="p-1.5 transition-colors"
              style={{
                color: colors.accent,
                backgroundColor: `${colors.accent}15`,
                borderRadius: sbr,
              }}
              title="New conversation"
            >
              <PlusCircle className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Conversation history drawer */}
        {historyOpen && (
          <div
            className="border-b overflow-y-auto"
            style={{
              backgroundColor: colors.sidebar,
              borderColor: `${colors.accent}33`,
              maxHeight: "280px",
            }}
          >
            <div className="px-3 py-2">
              <div
                className="flex items-center px-3 py-1.5 mb-2"
                style={{
                  backgroundColor: colors.chatBg,
                  borderRadius: sbr,
                  border: `1px solid ${colors.accent}30`,
                }}
              >
                <Search
                  className="w-3.5 h-3.5 mr-2 flex-shrink-0"
                  style={{ color: colors.accent }}
                />
                <input
                  value={historySearch}
                  onChange={(e) => setHistorySearch(e.target.value)}
                  placeholder="Search conversations..."
                  className="flex-1 bg-transparent outline-none text-xs"
                  style={{ color: colors.headings }}
                />
              </div>
              {filteredConversations.length === 0 ? (
                <p
                  className="text-xs text-center py-3"
                  style={{ color: colors.text, opacity: 0.5 }}
                >
                  No conversations found
                </p>
              ) : (
                <div className="space-y-1">
                  {filteredConversations.map((conv) => (
                    <div
                      key={conv.id}
                      className="flex items-center group"
                      style={{
                        backgroundColor:
                          conv.id === activeConvId
                            ? `${colors.accent}25`
                            : "transparent",
                        borderRadius: sbr,
                      }}
                    >
                      <button
                        onClick={() => switchConversation(conv.id)}
                        className="flex-1 text-left px-3 py-2 min-w-0"
                      >
                        <p
                          className="text-xs font-medium truncate"
                          style={{ color: colors.headings }}
                        >
                          {conv.title}
                        </p>
                        <p
                          className="text-xs truncate"
                          style={{ color: colors.text, opacity: 0.5 }}
                        >
                          {conv.messages.filter((m) => m.role === "user")
                            .length || 0}{" "}
                          messages
                          {conv.updatedAt &&
                            ` · ${new Date(conv.updatedAt).toLocaleDateString()}`}
                        </p>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeConversation(conv.id);
                        }}
                        className="p-1.5 opacity-0 group-hover:opacity-100 transition-opacity mr-1"
                        style={{ color: "#ef4444" }}
                        title="Delete conversation"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Messages */}
        <div
          className="flex-1 overflow-y-auto px-4 py-4 space-y-4"
          style={{ backgroundColor: colors.chatBg }}
        >
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"} items-end gap-2`}
            >
              {msg.role !== "user" && <BotAvatar />}
              <div className="max-w-[70%]">
                <div
                  className="px-4 py-3 leading-relaxed"
                  style={{
                    backgroundColor:
                      msg.role === "user"
                        ? colors.userBubble
                        : msg.isError
                          ? "#3b1515"
                          : colors.botBubble,
                    color: msg.isError ? "#f87171" : colors.headings,
                    border: `1px solid ${msg.isError ? "#ef444433" : colors.accent + "30"}`,
                    borderRadius:
                      msg.role === "user"
                        ? `${br} 0 ${br} ${br}`
                        : `0 ${br} ${br} ${br}`,
                    fontSize: `${fontSize}px`,
                    lineHeight: "1.7",
                  }}
                >
                  {msg.role === "assistant" &&
                  !msg.isError &&
                  /<[a-z][^>]*>/i.test(msg.content) ? (
                    <div
                      className="enriched-response"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(msg.content),
                      }}
                    />
                  ) : (
                    <FormatMsg text={msg.content} accentColor={colors.accent} />
                  )}
                </div>
                {/* Source citations */}
                {msg.sources && msg.sources.length > 0 && (
                  <SourceCitations
                    sources={msg.sources}
                    colors={colors}
                    borderRadius={borderRadius}
                  />
                )}
                {/* Feedback buttons for bot messages */}
                {msg.role === "assistant" && !msg.isError && i > 0 && (
                  <div className="flex items-center gap-1 mt-1.5 ml-1">
                    <button
                      onClick={() => handleFeedback(i, "up")}
                      className="p-1 rounded transition-colors"
                      style={{
                        color:
                          msg.feedback === "up"
                            ? "#22c55e"
                            : `${colors.text}50`,
                        backgroundColor:
                          msg.feedback === "up" ? "#22c55e15" : "transparent",
                      }}
                      title="Good response"
                    >
                      <ThumbsUp className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => handleFeedback(i, "down")}
                      className="p-1 rounded transition-colors"
                      style={{
                        color:
                          msg.feedback === "down"
                            ? "#ef4444"
                            : `${colors.text}50`,
                        backgroundColor:
                          msg.feedback === "down" ? "#ef444415" : "transparent",
                      }}
                      title="Poor response"
                    >
                      <ThumbsDown className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
              {msg.role === "user" && (
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                  style={{
                    backgroundColor: colors.userBubble,
                    color: colors.headings,
                    border: `1px solid ${colors.accent}40`,
                  }}
                >
                  {initials}
                </div>
              )}
            </div>
          ))}
          {sending && (
            <div className="flex justify-start items-end gap-2">
              <BotAvatar />
              <div
                className="px-4 py-3"
                style={{
                  backgroundColor: colors.botBubble,
                  border: `1px solid ${colors.accent}30`,
                  borderRadius: `0 ${br} ${br} ${br}`,
                }}
              >
                <div className="flex space-x-1.5">
                  {[0, 1, 2].map((d) => (
                    <div
                      key={d}
                      className="w-2 h-2 rounded-full animate-bounce"
                      style={{
                        backgroundColor: colors.accent,
                        animationDelay: `${d * 150}ms`,
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {/* Input bar */}
        <div
          className="px-4 py-3 flex space-x-3"
          style={{
            backgroundColor: colors.bg,
            borderTop: `1px solid ${colors.accent}33`,
          }}
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) =>
              e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())
            }
            placeholder="Ask a question..."
            disabled={sending}
            className="flex-1 px-4 py-2.5 outline-none"
            style={{
              backgroundColor: colors.chatBg,
              color: colors.headings,
              border: `1px solid ${colors.accent}40`,
              borderRadius: br,
              fontSize: `${fontSize}px`,
            }}
          />
          <button
            onClick={() => send()}
            disabled={sending || !input.trim()}
            className="p-2.5 transition-colors"
            style={{
              backgroundColor:
                !sending && input.trim() ? colors.accent : `${colors.accent}40`,
              color: "#fff",
              borderRadius: br,
              border: "none",
            }}
          >
            {sending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Routes Analytics Section ─────────────────────────────────────

const TASK_STATUS_COLORS = {
  open: "bg-amber-100 text-amber-800",
  in_progress: "bg-blue-100 text-blue-800",
  resolved: "bg-green-100 text-green-800",
  wont_fix: "bg-slate-100 text-slate-600",
};

const GapTasksPanel = ({ facility, user }) => {
  const fid = facility.facility_id;
  const [tasks, setTasks] = useState([]);
  const [clusters, setClusters] = useState(null);
  const [statusFilter, setStatusFilter] = useState("open");
  const [loading, setLoading] = useState(false);
  const [clustering, setClustering] = useState(false);
  const [clusterError, setClusterError] = useState("");
  const [qaTarget, setQaTarget] = useState(null);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    const data = await authFetch(
      `/rag/review-tasks?facility_id=${encodeURIComponent(fid)}&status=${statusFilter}`,
    );
    setTasks(Array.isArray(data) ? data : []);
    setLoading(false);
  }, [fid, statusFilter]);

  useEffect(() => {
    fetchTasks();
    setClusters(null);
  }, [fetchTasks]);

  const updateStatus = async (taskId, status) => {
    await authFetch("/rag/review-tasks/status", {
      method: "POST",
      body: JSON.stringify({ id: taskId, status }),
    });
    fetchTasks();
  };

  const runClustering = async () => {
    setClustering(true);
    setClusterError("");
    const data = await authFetch("/rag/review-tasks/cluster", {
      method: "POST",
      body: JSON.stringify({ facility_id: fid }),
    });
    if (data.clusters) {
      setClusters(data.clusters);
    } else {
      setClusterError(data.error || "Clustering failed");
    }
    setClustering(false);
    fetchTasks();
  };

  const openTasksByCluster = clusters
    ? clusters
    : tasks.reduce((acc, t) => {
        const label = t.cluster_label || "Unclustered";
        const g = acc.find((x) => x.label === label);
        if (g) g.tasks.push(t);
        else acc.push({ label, tasks: [t] });
        return acc;
      }, []);

  return (
    <div className="space-y-4">
      {/* Header toolbar */}
      <div className="flex items-center justify-between bg-white rounded-lg shadow-sm border border-slate-200 p-3">
        <div className="flex items-center space-x-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm"
          >
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="wont_fix">Won&apos;t Fix</option>
            <option value="all">All</option>
          </select>
          <button
            onClick={fetchTasks}
            className="p-1.5 text-slate-500 hover:text-slate-700"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          <span className="text-sm text-slate-500">
            {tasks.length} task{tasks.length !== 1 ? "s" : ""}
          </span>
        </div>
        <button
          onClick={runClustering}
          disabled={clustering || tasks.length === 0}
          className="flex items-center px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50"
        >
          {clustering ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <Brain className="w-4 h-4 mr-1.5" />
          )}
          {clustering ? "Clustering…" : "Cluster by Topic"}
        </button>
      </div>

      {clusterError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          {clusterError}
        </div>
      )}

      {tasks.length === 0 && !loading && (
        <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
          <Brain className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">
            No {statusFilter === "all" ? "" : statusFilter} gap tasks.
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Tasks are created automatically when the chatbot answers with low
            confidence (&lt; 0.70).
          </p>
        </div>
      )}

      {/* Clustered task list */}
      {openTasksByCluster.map((group) => (
        <div
          key={group.label}
          className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden"
        >
          <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-700">
              {group.label}
            </span>
            <span className="text-xs text-slate-400">
              {(clusters ? group.tasks : group.tasks).length} question
              {group.tasks.length !== 1 ? "s" : ""}
            </span>
          </div>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-slate-100">
              {group.tasks.map((task) => (
                <tr key={task.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-800 max-w-xs">
                    <p className="line-clamp-2">{task.question}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-slate-400">
                        Asked {task.times_surfaced}×
                      </span>
                      {task.confidence_floor != null && (
                        <span className="text-xs text-amber-600">
                          conf {task.confidence_floor.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${TASK_STATUS_COLORS[task.status] || "bg-slate-100 text-slate-600"}`}
                    >
                      {task.status.replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <div className="flex justify-end items-center gap-1">
                      <button
                        onClick={() =>
                          setQaTarget({
                            question: task.question,
                            taskId: task.id,
                          })
                        }
                        title="Create Q&A for this question"
                        className="px-2 py-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded hover:bg-emerald-100"
                      >
                        + Q&amp;A
                      </button>
                      {task.status === "open" && (
                        <button
                          onClick={() => updateStatus(task.id, "in_progress")}
                          className="px-2 py-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100"
                        >
                          Start
                        </button>
                      )}
                      {task.status !== "resolved" &&
                        task.status !== "wont_fix" && (
                          <button
                            onClick={() => updateStatus(task.id, "resolved")}
                            className="p-1 text-green-600 hover:bg-green-50 rounded"
                            title="Mark resolved"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        )}
                      {task.status === "open" && (
                        <button
                          onClick={() => updateStatus(task.id, "wont_fix")}
                          className="p-1 text-slate-400 hover:bg-slate-100 rounded"
                          title="Won't fix"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {/* Quick Q&A modal pre-filled from task */}
      {qaTarget && (
        <QuickQAModal
          facility={facility}
          user={user}
          initialQuestion={qaTarget.question}
          taskId={qaTarget.taskId}
          onClose={() => setQaTarget(null)}
          onSaved={() => {
            setQaTarget(null);
            updateStatus(qaTarget.taskId, "resolved");
          }}
        />
      )}
    </div>
  );
};

const QuickQAModal = ({
  facility,
  user,
  initialQuestion,
  taskId,
  onClose,
  onSaved,
}) => {
  const fid = facility.facility_id;
  const [form, setForm] = useState({
    question: initialQuestion || "",
    answer: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async () => {
    if (!form.answer.trim()) {
      setError("Answer is required.");
      return;
    }
    setSubmitting(true);
    setError("");
    const result = await authFetch("/rag/qa-pair", {
      method: "POST",
      body: JSON.stringify({
        facility_id: fid,
        question: form.question,
        answer: form.answer,
        metadata: { source: "gap_task", task_id: String(taskId) },
      }),
    });
    if (result.success) {
      onSaved();
    } else {
      setError(result.error || "Save failed");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-800">Answer this Gap</h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Question
            </label>
            <textarea
              value={form.question}
              onChange={(e) =>
                setForm((f) => ({ ...f, question: e.target.value }))
              }
              rows={2}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Answer *
            </label>
            <textarea
              value={form.answer}
              onChange={(e) =>
                setForm((f) => ({ ...f, answer: e.target.value }))
              }
              rows={5}
              autoFocus
              placeholder="Write the authoritative answer to add to the knowledge base…"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={submitting}
            className="flex items-center px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            Save to Knowledge Base
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Prompt Engineering Studio ─────────────────────────────────────
